import React, { useEffect, useState, useCallback } from "react";
import { Modal, ModalHeader, ModalBody, Spinner } from "reactstrap";
import {
  faAddressCard,
  faCreditCard,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import Stepper from "../../../Stepper";
import { craneInspectionsApi } from "../../../../services/inspectionServices";
import InformationModal from "../../../InformationModal";
import WorkOrderSelectionStep from "./MoveCraneInspection/WorOrderSelectionStep";
import TripSelectionStep from "./MoveCraneInspection/TripSelectionStep";
import ConfirmationStep from "./MoveCraneInspection/ConfirmationStep";
import {
  useWorkOrderDetails,
  ACTIONS,
} from "../../../../providers/workOrderDetailsProvider";

const STEPPER_STEPS = [
  { title: "Select Work Order", icon: faAddressCard },
  { title: "Select Trip & Technician", icon: faCalendarAlt },
  { title: "Confirm Details", icon: faCreditCard },
];

const StepperModal = ({
  isOpen,
  toggle,
  craneInspectionId,
  currentCraneInspection,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [, setWorkOrderDetails] = useWorkOrderDetails();

  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
    onClose: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!craneInspectionId) return;

      setLoading(true);
      try {
        const result = await craneInspectionsApi.getCraneInspectionDestinations(
          craneInspectionId
        );
        if (!result) {
          setInformationModal({
            isOpen: true,
            title: "Error",
            body: "No destination data received. Please try again.",
          });
          return;
        }
        setData(result);
      } catch (err) {
        setInformationModal({
          isOpen: true,
          title: "Error",
          body: "There was an error fetching the crane inspection destinations. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [craneInspectionId]);

  useEffect(() => {
    if (selectedWorkOrder) {
      setSelectedTrip(null);
    }
  }, [selectedWorkOrder]);

  const handleClose = useCallback(() => {
    if (isComplete) {
      setSelectedWorkOrder(null);
      setSelectedTrip(null);
      setIsComplete(false);
    }
    toggle();
  }, [isComplete, toggle]);

  const handleComplete = async () => {
    if (
      !selectedWorkOrder ||
      !selectedTrip ||
      !selectedTrip.selectedTechnician ||
      !selectedTrip.selectedDate
    ) {
      setInformationModal({
        isOpen: true,
        title: "Validation Error",
        body: "Please ensure all required fields are selected before completing the transfer.",
      });
      return;
    }

    setLoading(true);
    try {
      await craneInspectionsApi.moveCraneInspection({
        id: craneInspectionId,
        workOrderId: selectedWorkOrder.id,
        tripId: selectedTrip.id,
        technicianId: selectedTrip.selectedTechnician.id,
        date: selectedTrip.selectedDate.toISOString().split("T")[0],
      });

      setIsComplete(true);
      setInformationModal({
        isOpen: true,
        title: "Success",
        body: "Crane inspection has been successfully moved!",
        onClose: () => {
          handleClose();
          setWorkOrderDetails({
            action: ACTIONS.REFRESH,
          });
        },
      });
    } catch (err) {
      const errorMessage =
        err?.response?.data?.[0]?.msg ||
        err?.response?.data?.message ||
        "There was an error with your request. Please try again.";

      setInformationModal({
        isOpen: true,
        title: "Transfer Failed",
        body: errorMessage,
        onClose: handleClose,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWorkOrderSelect = (workOrder) => {
    setSelectedWorkOrder(workOrder);
  };

  const handleTripSelect = (trip) => {
    setSelectedTrip(trip);
  };

  const handleInformationModalClose = () => {
    if (informationModal.onClose) {
      informationModal.onClose();
    } else {
      setInformationModal({
        isOpen: false,
        title: "",
        body: "",
        onClose: null,
      });
    }
  };

  const isNextDisabled = (currentStep) => {
    const hasWorkOrders = data?.destinationWorkOrders?.length > 0;

    switch (currentStep) {
      case 0: // Work Order Selection Step
        return !hasWorkOrders || !selectedWorkOrder;
      case 1: // Trip Selection Step
        return (
          !selectedTrip ||
          !selectedTrip.selectedDate ||
          !selectedTrip.selectedTechnician
        );
      default:
        return false;
    }
  };

  if (informationModal.isOpen) {
    return (
      <InformationModal
        title={informationModal.title}
        body={informationModal.body}
        onClose={handleInformationModalClose}
      />
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>Crane Transfer Process</ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-3">Loading destination data...</p>
          </div>
        ) : (
          <Stepper
            steps={STEPPER_STEPS}
            onComplete={handleComplete}
            disableNext={isNextDisabled}
          >
            <WorkOrderSelectionStep
              destinationWorkOrders={data?.destinationWorkOrders}
              onSelectWorkOrder={handleWorkOrderSelect}
              selectedWorkOrder={selectedWorkOrder}
            />
            <TripSelectionStep
              selectedWorkOrder={selectedWorkOrder}
              onSelectTrip={handleTripSelect}
              currentCraneInspection={currentCraneInspection}
            />
            <ConfirmationStep
              selectedWorkOrder={selectedWorkOrder}
              selectedTrip={selectedTrip}
              selectedTechnician={selectedTrip?.selectedTechnician}
            />
          </Stepper>
        )}
      </ModalBody>
    </Modal>
  );
};

export default StepperModal;
